import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { fmtDate, money, openTop, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, StatusBadge, Spinner } from "@/components/shared";
import { useStore } from "@/context/StoreContext";
import { CreditCard, RefreshCw, ExternalLink, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const Btn = ({ children, ...p }) => (
  <button {...p} className="inline-flex items-center gap-2 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white font-semibold py-2.5 px-5 rounded-lg text-sm transition-colors">{children}</button>
);

function usePollCheckout(onDone) {
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("session_id");
  useEffect(() => {
    if (params.get("canceled")) { toast.info("Checkout canceled"); setParams({}); }
    if (!sessionId) { return; }
    let tries = 0;
    toast.loading("Confirming your subscription…", { id: "co" });
    const tick = async () => {
      try {
        const { data } = await api.get(`/billing/stripe/status/${sessionId}`);
        if (data.payment_status === "paid") { toast.success("Subscription active", { id: "co" }); setParams({}); onDone(); return; }
      } catch { /* retry */ }
      if (++tries < 15) setTimeout(tick, 2000);
      else toast.error("Payment not confirmed yet. Refresh in a minute.", { id: "co" });
    };
    tick();
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps
}

function ShopifyPlan({ b, reload }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">Your subscription is billed by Shopify and appears on your Shopify invoice. Choose or change a plan on Shopify's plan page.</p>
      {b.error && <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="billing-error">{b.error}</p>}
      <div className="flex flex-wrap gap-2">
        <Btn data-testid="shopify-choose-plan-btn" onClick={() => openTop(b.plan_selection_url)}><ExternalLink className="w-4 h-4" /> {b.status === "active" ? "Change plan" : "Choose a plan"}</Btn>
        <button data-testid="billing-refresh-btn" onClick={reload} className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-100 text-slate-700 font-semibold py-2.5 px-4 rounded-lg text-sm"><RefreshCw className="w-4 h-4" /> Refresh status</button>
      </div>
    </div>
  );
}

function IntervalToggle({ interval, setInterval }) {
  return (
    <div className="inline-flex rounded-lg border border-slate-200 p-1 bg-slate-50" data-testid="billing-interval-toggle">
      {[["month", "Monthly"], ["year", "Yearly · 2 months free"]].map(([k, label]) => (
        <button key={k} data-testid={`billing-interval-${k}`} onClick={() => setInterval(k)}
          className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-colors ${interval === k ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"}`}>{label}</button>
      ))}
    </div>
  );
}

function PlanCard({ p, interval, current, busy, onPick }) {
  const amount = p.prices[interval];
  const monthly = p.prices.month;
  return (
    <div className={`relative rounded-xl p-5 flex flex-col border ${p.recommended ? "border-emerald-500 ring-1 ring-emerald-500" : "border-slate-200"}`} data-testid={`stripe-plan-${p.handle}`}>
      {p.recommended && <span className="absolute -top-2.5 left-5 bg-emerald-600 text-white text-[11px] font-bold px-2 py-0.5 rounded-full">Recommended</span>}
      <p className="font-display font-bold text-slate-900">{p.name}</p>
      <p className="text-xs text-slate-500 mb-2">{p.tagline}</p>
      <p className="text-3xl font-display font-extrabold text-slate-900" data-testid={`stripe-plan-price-${p.handle}`}>{money(amount, p.currency)}<span className="text-sm font-medium text-slate-400">/{interval}</span></p>
      {interval === "year" && monthly && <p className="text-xs text-emerald-700 font-semibold" data-testid={`stripe-plan-savings-${p.handle}`}>Save {money(monthly * 12 - amount, p.currency)}/year · {money(amount / 12, p.currency)}/mo billed annually</p>}
      <ul className="text-sm text-slate-600 mt-3 space-y-1.5 flex-1">
        {p.features.map((f) => <li key={f} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />{f}</li>)}
      </ul>
      {current
        ? <p className="mt-4 text-sm font-semibold text-emerald-700" data-testid={`stripe-plan-current-${p.handle}`}>Your current plan</p>
        : onPick && <button data-testid={`stripe-subscribe-${p.handle}`} disabled={busy} onClick={() => onPick(p.handle)}
            className={`mt-4 w-full font-semibold py-2.5 rounded-lg text-sm disabled:opacity-50 transition-colors ${p.recommended ? "bg-emerald-700 hover:bg-emerald-800 text-white" : "border border-slate-300 hover:bg-slate-100 text-slate-800"}`}>Choose {p.name}</button>}
    </div>
  );
}

function StripePlan({ b }) {
  const [busy, setBusy] = useState(false);
  const [interval, setInterval] = useState(b.interval || "month");
  const post = async (path, extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(path, { origin_url: window.location.origin, ...extra });
      window.location.href = data.checkout_url || data.url;
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };
  if (b.complimentary) return <p className="text-sm text-slate-600" data-testid="billing-complimentary">This store has complimentary access. No payment is required.</p>;
  if (!b.plans?.length) return <p className="text-sm text-red-600" data-testid="billing-unconfigured">Subscription plans are not configured yet.</p>;
  const active = b.status === "active";
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <IntervalToggle interval={interval} setInterval={setInterval} />
        {active && <Btn data-testid="stripe-portal-btn" disabled={busy} onClick={() => post("/billing/stripe/portal")}><CreditCard className="w-4 h-4" /> Manage or change plan</Btn>}
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {b.plans.map((p) => (
          <PlanCard key={p.handle} p={p} interval={interval} busy={busy}
            current={active && b.plan_handle === p.handle && (b.interval || "month") === interval}
            onPick={active ? null : (plan) => post("/billing/stripe/checkout", { plan, interval })} />
        ))}
      </div>
      <p className="text-xs text-slate-400" data-testid="stripe-plan-tax-note">Prices in USD, plus applicable sales tax calculated at checkout. Cancel anytime.</p>
    </div>
  );
}

export default function Billing() {
  const [b, setB] = useState(null);
  const { refreshStore } = useStore();
  const load = useCallback(() => {
    setB(null);
    api.get("/billing").then((r) => setB(r.data)).catch((e) => setB({ fatal: formatApiErrorDetail(e.response?.data?.detail) }));
    refreshStore().catch(() => {});
  }, [refreshStore]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  usePollCheckout(load);

  if (!b) return <Spinner />;
  return (
    <div>
      <PageHeader title="Billing" subtitle="Your ConsignOut subscription" />
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-5" data-testid="billing-panel">
        {b.fatal ? <p className="text-sm text-red-600">{b.fatal}</p> : (
          <>
            <div className="grid sm:grid-cols-3 gap-4 text-sm bg-slate-50 rounded-lg p-4">
              <div><p className="text-xs text-slate-400">Status</p><div data-testid="billing-status"><StatusBadge status={b.status || "inactive"} /></div></div>
              <div><p className="text-xs text-slate-400">Plan</p><p className="font-medium text-slate-700" data-testid="billing-plan">{b.plan || "None"}</p></div>
              <div><p className="text-xs text-slate-400">Billed through</p><p className="font-medium text-slate-700" data-testid="billing-provider">{b.provider === "shopify" ? `Shopify (${b.shop})` : "Stripe"}</p></div>
            </div>
            {b.checked_at && <p className="text-xs text-slate-400">Last checked {fmtDate(b.checked_at)}</p>}
            {b.provider === "shopify" ? <ShopifyPlan b={b} reload={load} /> : <StripePlan b={b} />}
          </>
        )}
      </div>
    </div>
  );
}
